import { useEffect, useState } from "react";

import { AppLanguage, useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { config } from "config";

import German from "../../../i18n/de.json";
import English from "../../../i18n/en.json";

type Messages = Record<string, string | null>;

const BUNDLED: Record<AppLanguage, Messages> = {
  [AppLanguage.De]: German,
  [AppLanguage.En]: English,
};

export function useLocalizedMessages() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const [messages, setMessages] = useState<Messages>(() => BUNDLED[appLanguage]);

  useEffect(() => {
    setMessages(BUNDLED[appLanguage]);

    const cdnUrl = config.environment.I18N_CDN_URL;
    if (!cdnUrl) return;

    let controller: AbortController | null = null;

    const fetchAndOverlay = () => {
      controller?.abort();
      controller = new AbortController();
      fetch(`${cdnUrl}/${appLanguage}.json`, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((remote: Messages) => {
          setMessages({ ...BUNDLED[appLanguage], ...remote });
        })
        .catch(() => {
          /* network/CDN failure — bundled fallback already showing */
        });
    };

    fetchAndOverlay();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchAndOverlay();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", fetchAndOverlay);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", fetchAndOverlay);
      controller?.abort();
    };
  }, [appLanguage]);

  return messages;
}
